/**
 * Blocks — composite groups of elements that recur across real-estate layouts.
 *
 * A block owns its internal spacing and returns a flat element array plus the
 * y it finished at, so a template can stack blocks with a running cursor
 * instead of hand-computing every coordinate. Every element a block emits is
 * an ordinary editor element — nothing here is a special type the editor would
 * have to know about, so all of it stays individually selectable and editable.
 */

import { text, shape, rule, image } from './elements';
import { SPACE, TYPE, RULE, RADIUS } from './tokens';

/**
 * Downward scrim: a colour fading from transparent to solid, used to seat
 * white type over the lower part of a photograph so it stays legible whatever
 * image the user drops in.
 */
export const scrimDown = (id, { x, y, width, height, color, to = 1 }) =>
  shape(id, {
    x, y, width, height,
    fill: color,
    gradient: { type: 'linear', angle: 180, colors: [color, color], opacities: [0, to], positions: [0, 100] },
  });

/** Tracked-out label with a short accent rule beneath it. */
export const eyebrowWithRule = (idBase, r, { x, y, width, copy, color, ruleColor, ruleWidth = 380, align = 'left' }) => {
  const label = text(`${idBase}-eyebrow`, r.eyebrow, copy, { x, y, width, align }, { color });
  const y2 = y + label.height + SPACE.xs;
  return {
    elements: [label, rule(`${idBase}-eyebrow-rule`, { x, y: y2, width: ruleWidth, weight: RULE.medium, color: ruleColor })],
    y: y2 + RULE.medium,
  };
};

/**
 * Price plate — a filled block carrying the single number a buyer looks for
 * first. Sized to its copy so it never looks like a stretched button.
 */
export const priceBadge = (idBase, r, { x, y, copy, fill, inkColor, width = 820, height = 200 }) => {
  const plate = shape(`${idBase}-price-plate`, { x, y, width, height, fill, cornerRadius: RADIUS.none });
  const label = text(
    `${idBase}-price`, r.h2, copy,
    { x, y: y + (height - TYPE.h2 * 1.25) / 2, width, align: 'center' },
    { color: inkColor },
  );
  return { elements: [plate, label], y: y + height };
};

/**
 * Specification row — the beds / baths / sq ft line. Columns are computed from
 * the available width so the row stays optically even at any count.
 */
export const statRow = (idBase, r, { x, y, width, stats, valueColor, labelColor, dividerColor }) => {
  const cols = stats.length;
  // Columns carry a real gutter rather than butting together: text boxes are
  // 8px wider than their measure on each side (TEXT_INSET), so touching
  // columns would overlap — and a gutter is what makes the row read as
  // separate figures rather than one run-on line.
  const gutter = SPACE.sm;
  const colW = Math.floor((width - gutter * (cols - 1)) / cols);
  const elements = [];
  let bottom = y;

  stats.forEach((s, i) => {
    const cx = x + i * (colW + gutter);
    const value = text(`${idBase}-stat${i}-value`, r.stat, s.value, { x: cx, y, width: colW, align: 'center' }, { color: valueColor });
    const labelY = y + value.height + SPACE.xxs;
    const label = text(`${idBase}-stat${i}-label`, r.statLabel, s.label, { x: cx, y: labelY, width: colW, align: 'center' }, { color: labelColor });
    elements.push(value, label);
    bottom = Math.max(bottom, labelY + label.height);

    // Hairline between columns, inset vertically so it reads as a separator
    // rather than a border.
    if (i > 0 && dividerColor) {
      elements.push(shape(`${idBase}-stat${i}-div`, {
        x: cx - Math.round(gutter / 2), y: y + SPACE.xxs, width: RULE.hair,
        height: value.height + SPACE.xxs + TYPE.caption, fill: dividerColor,
      }));
    }
  });

  return { elements, y: bottom };
};

/**
 * Agent lockup — headshot, name, title, and contact lines. The photo is a
 * circular image so it stays a drop-in replacement for the user's own.
 */
export const agentLockup = (idBase, r, { x, y, width, photoSrc, name, title, lines, nameColor, textColor, align = 'left' }) => {
  const photoSize = 260;
  const elements = [];

  if (photoSrc) {
    elements.push(image(`${idBase}-agent-photo`, {
      x, y, width: photoSize, height: photoSize, src: photoSrc, cornerRadius: photoSize / 2,
    }));
  }

  const textX = photoSrc ? x + photoSize + SPACE.sm : x;
  const textW = width - (photoSrc ? photoSize + SPACE.sm : 0);

  const nameEl = text(`${idBase}-agent-name`, r.h3, name, { x: textX, y, width: textW, align }, { color: nameColor });
  const titleY = y + nameEl.height + SPACE.xxs;
  const titleEl = text(`${idBase}-agent-title`, r.statLabel, title, { x: textX, y: titleY, width: textW, align }, { color: textColor });
  const contactY = titleY + titleEl.height + SPACE.xs;
  const contactEl = text(`${idBase}-agent-contact`, r.body, lines.join('\n'), { x: textX, y: contactY, width: textW, align }, { color: textColor });

  elements.push(nameEl, titleEl, contactEl);
  return { elements, y: Math.max(y + (photoSrc ? photoSize : 0), contactY + contactEl.height) };
};

/** Full-width footer bar with a single line of contact copy. */
export const contactBar = (idBase, r, { x, y, width, height, copy, fill, inkColor }) => {
  const bar = shape(`${idBase}-contactbar`, { x, y, width, height, fill });
  const label = text(
    `${idBase}-contactbar-copy`, r.cta, copy,
    { x: x + SPACE.md, y: y + (height - TYPE.body * 1.4) / 2, width: width - SPACE.md * 2, align: 'center' },
    { color: inkColor },
  );
  return { elements: [bar, label], y: y + height };
};
